// src/components/GoogleCallback.js
import { useEffect, useContext, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { StoreContext } from '../context/StoreContext';
import { FaSpinner } from 'react-icons/fa';

function GoogleCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setToken, setUser } = useContext(StoreContext);
  const processed = useRef(false);

  useEffect(() => {
    // Prevent double execution in React Strict Mode
    if (processed.current) return;
    processed.current = true;

    const params = new URLSearchParams(location.search);
    const token = params.get('customer_token');
    const userParam = params.get('user');

    console.log('🔐 GoogleCallback - token:', token ? 'present' : 'missing');
    console.log('👤 GoogleCallback - userParam:', userParam ? 'present' : 'missing');

    // Token is mandatory
    if (!token) {
      console.error('❌ No token in URL');
      navigate('/login?error=google_auth_failed');
      return;
    }

    // Store token first (same key as normal login)
    localStorage.setItem('customer_token', token);
    setToken(token);

    let parsedUser = null;
    let missingMobile = false;

    if (userParam) {
      try {
        parsedUser = JSON.parse(decodeURIComponent(userParam));
       
        // Ensure address object exists even if not provided
        if (!parsedUser.address) {
          parsedUser.address = {
            street: '',
            city: '',
            state: '',
            postalCode: '',
            country: ''
          };
        }

        // Check if mobile is missing (empty string, null, or undefined)
        // Backend now omits mobile for Google users, so it may be undefined or null
        missingMobile = !parsedUser.mobile || parsedUser.mobile.trim() === '';

        // Store user in localStorage and context
        localStorage.setItem('user', JSON.stringify(parsedUser));
        setUser(parsedUser);

        console.log('✅ User stored:', {
          email: parsedUser.email,
          name: `${parsedUser.firstName || ''} ${parsedUser.middleName || ''} ${parsedUser.lastName || ''}`.trim(),
          mobile: parsedUser.mobile || '(not set)',
          missingMobile,
          hasAddress: !!parsedUser.address?.street
        });
      } catch (err) {
        console.error('❌ Failed to parse user data', err);
        // Even if parsing fails, we have a token, but user data is incomplete.
        // Redirect to profile completion to collect missing info.
        missingMobile = true;
      }
    } else {
      // No user data in URL – this shouldn't happen with current backend,
      // but handle gracefully by redirecting to profile completion.
      console.warn('⚠️ No user data received from backend');
      missingMobile = true;
    }

    // Redirect to profile completion if mobile number is missing
    if (missingMobile) {
      const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
      navigate(`/complete-profile?returnTo=${returnTo}`);
      return;
    }

    // Get redirect path or go home
    const redirectPath = localStorage.getItem('postLoginRedirect');
    localStorage.removeItem('postLoginRedirect');
    const destination = redirectPath || '/';
    console.log('🔄 Redirecting to:', destination);

    // Small delay ensures React context updates are applied before navigation
    setTimeout(() => {
      try {
        navigate(destination);
      } catch (err) {
        // Fallback if navigate fails (e.g., outside router context)
        console.warn('navigate() failed, using window.location', err);
        window.location.href = destination;
      }
    }, 100);
  }, [location, navigate, setToken, setUser]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      gap: '1rem'
    }}>
      <FaSpinner className="fa-spin" size={32} color="#007bff" />
      <p>Completing Google sign in...</p>
    </div>
  );
}

export default GoogleCallback;